import "../App.css";

import { useState, useEffect, useCallback, useRef, useMemo } from "react";

import PersonalDetailsSection from "../components/PersonalDetailsSection";

import { useNavigate } from "react-router-dom";

import type {
  Profile,
  SideProject,
  SectionTypeDescription,
  ProfileLinkConfig,
  Branding,
  AccountProfile,
  ProjectPhoto,
  ProfileContactDetails,
  ContentSuggestionChatMessage,
  ContentSuggestionRequestBody,
  ProfileLinkResponseBody,
} from "../types";
import {
  createGuestUser,
  createProfileLinkQuery,
  fetchProfileFromLinkId,
  updateProfileVisit,
} from "../requests";
import SideProjectSection from "../components/SideProjectSection";
import defaultMainLogo from "../assets/default-main-logo.png";
import defaultIconLogo from "../assets/default-icon-logo.png";
import "../components/components.css";
import { darkModeBlack } from "../colors";
import { useMediaQuery } from "react-responsive";
import { Dialog } from "@material-ui/core";
import ProjectPhotoViewer from "../components/ProjectPhotoViewer";
import WorkplaceSection from "../components/WorkplaceSection";
import EditorTitle from "../components/EditorTitle";
import { useParams } from "react-router-dom";
import { LeadButton } from "./dashboard/home/components/LeadButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhone,
  faAt,
  faLink,
  faComment,
  faFilePdf,
  faPaintBrush,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import vocsLogo from "../assets/voc_studio_logo.png";

import { toast } from "sonner";

import {
  Input,
  trim,
  InputRef,
  Button,
  LoadingSpinner,
  Image,
} from "../components";

import { motion } from "framer-motion";
import QRCode from "react-qr-code";

import EducationSection from "../components/EducationSection";
import SkillsSection from "../components/SkillsSection";

import ProfileContainer from "./profile/ProfileContainer";
import { useStatsigClient } from "../hooks";
import { SalesContactDrawer } from "./profile/SalesContactDrawer";
import { salesContactState } from "../state/salesContact";

interface RouteParams {
  [key: string]: string | undefined;
  profileLinkId?: string;
}

function ProfileViewer() {
  const { profileLinkId } = useParams<RouteParams>();

  const navigate = useNavigate();
  const [darkModeActive, setDarkModeActive] = useState<boolean>(false);
  const [pdfMode, setPdfMode] = useState<boolean>(false);

  const [profileLink, setProfileLink] = useState("");
  const [linkCreatorProfileId, setLinkCreatorProfileId] = useState("");
  const [viewDialogFullScreen, setViewDialogFullScreen] =
    useState<boolean>(false);
  const [isManager, setIsManager] = useState<boolean>(false);
  const [profilePhotoId, setProfilePhotoId] = useState<string>("");
  const [accountProfile, setAccountProfile] = useState<AccountProfile | null>(
    null,
  );

  const statsigClient = useStatsigClient();

  const [selectedProjectPhotoAssetId, setSelectedProjectPhotoAssetId] =
    useState<string>("");
  const [profileLinkConfig, setProfileLinkConfig] =
    useState<ProfileLinkConfig | null>(null);
  const [profileLinkResponseBodies, setProfileLinkResponseBodies] = useState<
    ProfileLinkResponseBody[]
  >([]);

  const [currentProfileSideProjects, setCurrentProfileSideProjects] = useState<
    SideProject[]
  >([]);
  const [profileContactDetails, setProfileContactDetails] =
    useState<ProfileContactDetails | null>(null);
  const [profile, setProfile] = useState<Profile>({
    id: "",
    profile_name: "",
    name: "",
    title: "",
    pitch: "",
    ui_config: { dark_mode: false, use_profile_picture: true },
    roles_section: { order: 0, title: "", roles: [] },
    skills_section: { order: 0, title: "", skill_blocks: [] },
    side_projects_section: { order: 0, title: "", side_projects: [] },
    profile_tags: [],
    workplaces: [],
    educations: [],
  });

  const isMobile = useMediaQuery({ query: "(max-width: 760px)" });

  const inputRef = useRef<InputRef>(null);
  const profileLinkQuestionToastId = useRef<string | number>();
  const [additionalPitches, setAdditionalPitches] = useState<string[]>([]);
  const [profileNotAvailable, setProfileNotAvailable] =
    useState<boolean>(false);

  const [currentQuestion, setCurrentQuestions] = useState<string>("");
  const [currentAnswer, setCurrentAnswer] = useState<string>("");
  const [isLoadingProfileLinkQuestion, setIsLoadingProfileLinkQuestion] =
    useState<boolean>(false);

  const firstRender = useRef(true);

  const handleAddComment = useCallback(
    (value: string) => {
      setCurrentQuestions(value);
      let messages: ContentSuggestionChatMessage[] = [
        { order: 0, content: value, fromUser: true },
      ];
      if (profileLinkId) {
        profileLinkQuestionToastId.current = toast.loading(
          "Checking profile for answers...",
        );
        setIsLoadingProfileLinkQuestion(true);

        createProfileLinkQuery(profileLinkId, messages).then(
          (resp: ContentSuggestionRequestBody) => {
            toast.success("Done!", { id: profileLinkQuestionToastId.current });
            setCurrentAnswer(resp.messages[1].content);
            setIsLoadingProfileLinkQuestion(false);
          },
        );
      }

      inputRef.current?.clear();
    },
    [profileLinkId],
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (inputRef.current?.value) handleAddComment(inputRef.current?.value);
    },
    [handleAddComment],
  );

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const pdfParam = queryParams.get("pdf");
    setPdfMode(pdfParam === "true");

    if (profileLinkId !== undefined) {
      fetchProfileFromLinkId(profileLinkId)
        .then((profileLinkResponseBody) => {
          const accountProfile = profileLinkResponseBody.accountProfile;
          setProfileContactDetails(
            profileLinkResponseBody.profileContactDetails,
          );
          setProfile(accountProfile.profile);
          setProfilePhotoId(accountProfile.profilePhotoId ?? "");
          setLinkCreatorProfileId(accountProfile.linkCreatedByProfileId ?? "");
          setProfileLink(profileLinkId);
          setAccountProfile(accountProfile);
          setIsManager(accountProfile.isManager ?? false);
          if (accountProfile.linkConfig) {
            setDarkModeActive(accountProfile.linkConfig.darkMode);
            setProfileLinkConfig(accountProfile.linkConfig);
            if (accountProfile.linkConfig.dynamic) {
              if (localStorage.getItem("op-guest-user-id") != null) {
                navigate(`/links/${profileLinkId}?dynamic=true`);
              } else {
                createGuestUser().then((guestUserId) => {
                  localStorage.setItem("op-guest-user-id", guestUserId);
                  navigate(`/links/${profileLinkId}?dynamic=true`);
                });
              }
            }

            let additional: string[] = [];

            if (
              accountProfile.linkConfig.showAssignmentPitch &&
              accountProfile.linkConfig.assignmentPitch !== ""
            ) {
              additional.push(accountProfile.linkConfig.assignmentPitch);
            }

            if (
              accountProfile.linkConfig.showCustomPitch &&
              accountProfile.linkConfig.customPitch !== ""
            ) {
              additional.push(accountProfile.linkConfig.customPitch);
            }

            setAdditionalPitches(additional);
          }

          const sortedSideProjects =
            accountProfile.profile.side_projects_section?.side_projects?.sort(
              (a: SideProject, b: SideProject) => {
                return a.order - b.order;
              },
            ) || [];

          if (firstRender.current) {
            firstRender.current = false;

            const lastVisitTime = localStorage.getItem(
              `lastVisit_${profileLinkId}`,
            );
            const shouldUpdateVisit =
              !lastVisitTime || hasHourPassed(parseInt(lastVisitTime));

            if (shouldUpdateVisit) {
              updateProfileVisit("", profileLinkId).then(() => {
                localStorage.setItem(
                  `lastVisit_${profileLinkId}`,
                  Date.now().toString(),
                );
              });
            }
          }

          setCurrentProfileSideProjects(sortedSideProjects);
        })
        .catch((err) => {
          if (err.response.data === "LINK_ARCHIVED\n") {
            setProfileNotAvailable(true);
          }
        });
    }
  }, []);

  function hasHourPassed(lastVisitTime: number): boolean {
    const oneHour = 60 * 60 * 1000; // one hour in milliseconds
    return Date.now() - lastVisitTime > oneHour;
  }

  function getAssetUrl(branding: Branding | null) {
    if (branding === null) return null;
    if (branding.logotypes.length > 0) {
      return branding.logotypes[0].assetUrl;
    }
    return null;
  }

  function getAssetSize(branding: Branding | null) {
    if (branding === null) return null;
    if (branding.logotypes.length > 0) {
      return branding.logotypes[0].size;
    }
    return null;
  }

  const openWebsite = (url: string) => {
    let websiteUrl = url;
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      websiteUrl = "https://" + url;
    }
    window.open(websiteUrl, "_blank");
  };

  function onCopyToClipboard(value: string, text: string) {
    navigator.clipboard.writeText(value).then(
      () => {
        toast.success(text);
      },
      () => {
        /* clipboard write failed */
      },
    );
  }

  const getSectionsInOrder = () => {
    const availableSections: SectionTypeDescription[] = [];
    if (profile.roles_section !== null && profile.roles_section !== undefined) {
      const role: SectionTypeDescription = {
        title: "Work Experience",
        info: "Describe your previous roles and workplaces",
        type: "roles",
        order: profile.roles_section!.order,
      };
      availableSections.push(role);
    }

    if (
      profile.skills_section !== null &&
      profile.skills_section !== undefined
    ) {
      const skills: SectionTypeDescription = {
        title: "Skills",
        info: "Express your skills",
        type: "skills",
        order: profile.skills_section!.order,
      };
      availableSections.push(skills);
    }

    if (
      profile.side_projects_section !== null &&
      profile.side_projects_section !== undefined &&
      profile.side_projects_section.side_projects !== null &&
      profile.side_projects_section.side_projects.length > 0
    ) {
      const skills: SectionTypeDescription = {
        title: "Projects",
        info: "Add any other project that does not fit under work experience",
        type: "side-projects",
        order: profile.side_projects_section!.order,
      };
      availableSections.push(skills);
    }

    const sectionsInOrder = availableSections.sort((a, b) => a.order - b.order);
    return sectionsInOrder;
  };

  const sortedWorkplaces = useMemo(() => {
    const editedWorkplaces = profileLinkConfig?.editedWorkplaces ?? [];

    if (profile.workplaces) {
      return profile?.workplaces.map((wp) => {
        const editedWorkplace = editedWorkplaces.find((w) => w.id === wp.id);
        return editedWorkplace ?? wp;
      });
    } else {
      return [];
    }
  }, [profileLinkConfig]);

  const sortedProjects = useMemo(() => {
    if (
      !profile?.side_projects_section ||
      !profile?.side_projects_section.side_projects
    ) {
      return [];
    }
    const editedProjects = profileLinkConfig?.editedSideProjects ?? [];

    return profile?.side_projects_section?.side_projects.map((wp) => {
      const editedProject = editedProjects.find((w) => w.id === wp.id);
      return editedProject ?? wp;
    });
  }, [profileLinkConfig]);

  if (profileNotAvailable) {
    return (
      <div className="w-full flex items-center justify-center h-full flex-col">
        <h3 className="font-medium text-xl">
          Profile is no longer available for viewing
        </h3>
        <img className="max-w-44 mt-4" src={vocsLogo} alt="VOCS" />
      </div>
    );
  }

  if (profileLinkConfig === null) {
    return (
      <div className="flex flex-1 h-[800px] flex-col justify-center align-center ">
        <div>
          <LoadingSpinner size="medium" />
        </div>
      </div>
    );
  }
  const linkConfig = profileLinkConfig;
  const logoAssetUrl = getAssetUrl(linkConfig.branding || null);
  return (
    <div
      id="wrapper"
      style={{
        color: darkModeActive ? "white" : darkModeBlack,
        backgroundColor: darkModeActive ? darkModeBlack : "#fff",
      }}
    >
      <motion.div
        className="flex pt-[60px]"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
      >
        {profileContactDetails && !pdfMode && !isMobile ? (
          <div
            className={
              "sticky top-[60px] h-screen w-[300px] left-0 overflow-y-auto z-50 flex-shrink-0"
            }
          >
            <div className="h-full">
              <div className="flex justify-center items-center px-8">
                <img
                  alt="logo"
                  src={
                    profileContactDetails?.logoId
                      ? "https://pub-4b15203a4a0f4b26ab71deeffce0164a.r2.dev/" +
                        profileContactDetails?.logoId
                      : defaultMainLogo
                  }
                  className={`object-scale-contain`}
                  style={{
                    maxHeight: 100,
                  }}
                />
              </div>

              {profileContactDetails.shortDesc && (
                <div className="w-full flex flex-col pt-5 pb-5 justify-center items-center px-8 text-gray-6">
                  <p className="text-xs">{profileContactDetails.shortDesc}</p>
                </div>
              )}

              <div className="flex flex-col w-full pt-4 items-center pl-4 gap-1">
                <LeadButton
                  onClick={() => {
                    openWebsite(profileContactDetails.website);
                  }}
                >
                  {profileContactDetails.iconLogoId ? (
                    <img
                      alt="logo"
                      src={
                        "https://pub-4b15203a4a0f4b26ab71deeffce0164a.r2.dev/" +
                        profileContactDetails?.iconLogoId
                      }
                      className={`object-scale-contain w-10 h-10`}
                    />
                  ) : (
                    <img
                      alt="logo"
                      src={defaultIconLogo}
                      className={`object-scale-contain w-10 h-10`}
                    />
                  )}
                  <p className="truncate w-full">
                    Visit {profileContactDetails.name}
                  </p>
                </LeadButton>
                {(profileContactDetails?.phoneNumber ||
                  profileContactDetails?.salesContact) && (
                  <LeadButton
                    onClick={() => {
                      if (profileContactDetails?.salesContact) {
                        onCopyToClipboard(
                          profileContactDetails.salesContact,
                          "Sales contact copied to clipboard",
                        );
                      } else if (profileContactDetails.phoneNumber) {
                        onCopyToClipboard(
                          profileContactDetails.phoneNumber,
                          "Phone number copied to clipboard",
                        );
                      }
                    }}
                  >
                    <FontAwesomeIcon
                      width={20}
                      height={20}
                      icon={faPhone}
                      className="w-5 h-5 shrink-0"
                    />
                    <div className={`flex flex-col truncate w-full`}>
                      {profileContactDetails?.salesContact && (
                        <p className="truncate w-full">
                          {profileContactDetails.salesContact}
                        </p>
                      )}
                      {profileContactDetails.phoneNumber && (
                        <p className="truncate w-full">
                          {profileContactDetails.phoneNumber}
                        </p>
                      )}
                    </div>
                  </LeadButton>
                )}
                <LeadButton
                  onClick={() => {
                    onCopyToClipboard(
                      profileContactDetails.email,
                      "Email copied to clipboard",
                    );
                  }}
                >
                  <FontAwesomeIcon
                    width={20}
                    height={20}
                    icon={faAt}
                    className="w-5 h-5 shrink-0"
                  />
                  <p className="truncate w-full">
                    {profileContactDetails?.email}
                  </p>
                </LeadButton>
                <LeadButton
                  className={"w-100 xl:w-100"}
                  onClick={() => {
                    onCopyToClipboard(
                      window.location.href,
                      "Profile Version link copied!",
                    );
                  }}
                >
                  <FontAwesomeIcon
                    width={20}
                    height={20}
                    icon={faLink}
                    className="w-5 h-5 shrink-0"
                  />
                  <p className="truncate w-full">
                    {profileLinkResponseBodies.length > 1
                      ? "Copy link to these profiles"
                      : "Copy link to this profile"}
                  </p>
                </LeadButton>
                <LeadButton
                  className={"w-100 xl:w-100"}
                  onClick={async () => {
                    // Log the PDF download event

                    var pdfUrl = "https://profile-pdf-exporter.onrender.com";
                    var mainUrl = "https://voc.studio";
                    /*
                    if (
                      window.location.origin.indexOf("localhost") !== -1
                    ) {
                      pdfUrl = "http://localhost:4000";
                    }
                     */

                    const downloadUrl =
                      pdfUrl + `/render?url=${mainUrl}/pdf/${profileLinkId}`;

                    let name = profile.name;

                    const toastId = toast.loading(`Downloading PDF...`);

                    try {
                      const response = await fetch(downloadUrl, {
                        method: "GET",
                        headers: {
                          Accept: "application/pdf",
                          Name: name,
                          x_api_key: process.env.REACT_APP_X_API_KEY ?? "",
                          // Add any custom headers here if needed
                          // 'Authorization': 'Bearer your-token',
                        },
                      });

                      if (response.ok) {
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `${name}.pdf`;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                      } else {
                        console.error(
                          "PDF download failed:",
                          response.status,
                          response.statusText,
                        );
                      }
                    } catch (error) {
                      console.error("PDF download error:", error);
                    }

                    toast.success(`PDF downloaded successfully!`, {
                      id: toastId,
                    });
                  }}
                >
                  <FontAwesomeIcon
                    width={20}
                    height={20}
                    icon={faFilePdf}
                    className="w-5 h-5 shrink-0"
                  />
                  <p className="truncate w-full">Download as PDF</p>
                </LeadButton>

                {linkConfig.allowCustomContact && (
                  <LeadButton
                    className={"w-100 xl:w-100"}
                    onClick={() => {
                      salesContactState.showAddSalesContact = true;
                    }}
                  >
                    <FontAwesomeIcon
                      width={20}
                      height={20}
                      icon={faPaintBrush}
                      className="w-5 h-5 shrink-0"
                    />
                    <p className="truncate w-full">
                      Edit logo and sales contact
                    </p>
                  </LeadButton>
                )}
              </div>
            </div>
          </div>
        ) : null}

        <div className="profile-viewer-content">
          <div>
            <SalesContactDrawer
              onAddSalesContact={() => {}}
              profileContactDetails={profileContactDetails}
              profileLinkId={profileLinkId}
            >
              <ProfileContainer
                profileLinkId={profileLinkId!}
                isEditable={false}
                pdfMode={pdfMode}
              />
            </SalesContactDrawer>
            {isMobile && profileContactDetails && !pdfMode ? (
              <div className={"w-full mt-12"}>
                <div className="h-full">
                  <div className="flex justify-center items-center px-8">
                    <img
                      alt="logo"
                      src={
                        profileContactDetails?.logoId
                          ? "https://pub-4b15203a4a0f4b26ab71deeffce0164a.r2.dev/" +
                            profileContactDetails?.logoId
                          : defaultMainLogo
                      }
                      className={`object-scale-contain`}
                      style={{
                        maxHeight: 100,
                      }}
                    />
                  </div>

                  <div className="flex flex-col w-full pt-4 items-center pl-4 gap-1">
                    <LeadButton
                      onClick={() => {
                        openWebsite(profileContactDetails.website);
                      }}
                    >
                      {profileContactDetails.iconLogoId ? (
                        <img
                          alt="logo"
                          src={
                            "https://pub-4b15203a4a0f4b26ab71deeffce0164a.r2.dev/" +
                            profileContactDetails?.iconLogoId
                          }
                          className={`object-scale-contain w-10 h-10`}
                        />
                      ) : (
                        <img
                          alt="logo"
                          src={defaultIconLogo}
                          className={`object-scale-contain w-10 h-10`}
                        />
                      )}
                      <p className="truncate w-full">
                        Visit {profileContactDetails.name}
                      </p>
                    </LeadButton>
                    {(profileContactDetails?.phoneNumber ||
                      profileContactDetails?.salesContact) && (
                      <LeadButton
                        onClick={() => {
                          if (profileContactDetails?.salesContact) {
                            onCopyToClipboard(
                              profileContactDetails.salesContact,
                              "Sales contact copied to clipboard",
                            );
                          } else if (profileContactDetails.phoneNumber) {
                            onCopyToClipboard(
                              profileContactDetails.phoneNumber,
                              "Phone number copied to clipboard",
                            );
                          }
                        }}
                      >
                        <FontAwesomeIcon
                          width={20}
                          height={20}
                          icon={faPhone}
                          className="w-5 h-5 shrink-0"
                        />
                        <div className={`flex flex-col truncate w-full`}>
                          {profileContactDetails?.salesContact && (
                            <p className="truncate w-full">
                              {profileContactDetails.salesContact}
                            </p>
                          )}
                          {profileContactDetails.phoneNumber && (
                            <p className="truncate w-full">
                              {profileContactDetails.phoneNumber}
                            </p>
                          )}
                        </div>
                      </LeadButton>
                    )}
                    <LeadButton
                      onClick={() => {
                        onCopyToClipboard(
                          profileContactDetails.email,
                          "Email copied to clipboard",
                        );
                      }}
                    >
                      <FontAwesomeIcon
                        width={20}
                        height={20}
                        icon={faAt}
                        className="w-5 h-5 shrink-0"
                      />
                      <p className="truncate w-full">
                        {profileContactDetails?.email}
                      </p>
                    </LeadButton>
                    <LeadButton
                      className={"w-100 xl:w-100"}
                      onClick={() => {
                        onCopyToClipboard(
                          window.location.href,
                          "Profile Version link copied!",
                        );
                      }}
                    >
                      <FontAwesomeIcon
                        width={20}
                        height={20}
                        icon={faLink}
                        className="w-5 h-5 shrink-0"
                      />
                      <p className="truncate w-full">
                        {profileLinkResponseBodies.length > 1
                          ? "Copy link to these profiles"
                          : "Copy link to this profile"}
                      </p>
                    </LeadButton>
                    <LeadButton
                      className={"w-100 xl:w-100"}
                      onClick={() => {
                        onCopyToClipboard(
                          window.location.href,
                          "Profile Version link copied!",
                        );
                      }}
                    >
                      <FontAwesomeIcon
                        width={20}
                        height={20}
                        icon={faLink}
                        className="w-5 h-5 shrink-0"
                      />
                      <p className="truncate w-full">
                        Edit logo and sales contact
                      </p>
                    </LeadButton>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {profileContactDetails && pdfMode && profileLink ? (
            <div className="flex flex-col w-full">
              <div className="w-full min-h-[2px] bg-gray-2 mb-12 mt-4" />
              <div className="flex flex-1 flex-row">
                <img
                  key="org-logo-pdf"
                  src={
                    "https://pub-4b15203a4a0f4b26ab71deeffce0164a.r2.dev/" +
                    profileContactDetails.iconLogoId +
                    "?nocache=12345"
                  }
                  className={`object-scale-contain mr-4`}
                  crossOrigin="anonymous"
                  alt="logo"
                  style={{ maxHeight: 100 }}
                />

                {(profileContactDetails.phoneNumber ||
                  profileContactDetails?.salesContact) && (
                  <div className="flex flex-row items-center">
                    <FontAwesomeIcon
                      width={24}
                      height={24}
                      icon={faPhone}
                      style={{ minHeight: "38px" }}
                      className={"w-5 h-5 shrink-0"}
                    />
                    <div
                      className={"flex flex-col ml-2"}
                      onClick={() => {
                        if (profileContactDetails?.salesContact) {
                          onCopyToClipboard(
                            profileContactDetails.salesContact,
                            "Contact information copied to clipboard",
                          );
                        } else if (profileContactDetails.phoneNumber) {
                          onCopyToClipboard(
                            profileContactDetails.phoneNumber,
                            "Phone number copied to clipboard",
                          );
                        }
                      }}
                    >
                      <p>Contact</p>
                      <div
                        className={`w-full ${!profileContactDetails?.salesContact ? "flex items-center" : ""}`}
                      >
                        {profileContactDetails?.salesContact && (
                          <p>{profileContactDetails.salesContact}</p>
                        )}
                        {profileContactDetails.phoneNumber && (
                          <p>{profileContactDetails?.phoneNumber}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex flex-row items-center ml-[24px]">
                  <FontAwesomeIcon
                    width={24}
                    height={24}
                    icon={faAt}
                    style={{ minHeight: "38px" }}
                    className={"w-5 h-5 shrink-0"}
                  />
                  <div
                    className={"flex flex-col ml-2"}
                    onClick={() => {
                      onCopyToClipboard(
                        profileContactDetails.email,
                        "Email copied to clipboard",
                      );
                    }}
                  >
                    <p>Mail</p>
                    <p className="w-full">{profileContactDetails?.email}</p>
                  </div>
                </div>
              </div>
              <div className="w-full min-h-[2px] bg-gray-2 mb-12 mt-12" />
              <div className="flex flex-1 flex-row px-8 justify-center items-center mt-2">
                <QRCode
                  size={62}
                  value={`https://voc.studio/p/${profileLinkId}`}
                  className="rounded-2xl w-16 h-16 mr-4"
                />
                <p
                  className={pdfMode ? "mb-4" : ""}
                >{`Visit ${profile.name}´s profile link!`}</p>
              </div>
              <div className="flex flex-1 justify-end">
                <div className="flex flex-col min-h-[100px] max-h-[100px] max-w-[100px] justify-end">
                  <span className="text-xs text-red-3 font-medium mb-[4px]">
                    Powered by
                  </span>
                  <img src={vocsLogo} alt="VOCS" />
                </div>
              </div>
            </div>
          ) : null}
          <div className="min-h-[250px]" />
        </div>
      </motion.div>
      {selectedProjectPhotoAssetId !== "" ? (
        <Dialog
          fullWidth
          fullScreen={viewDialogFullScreen}
          open={selectedProjectPhotoAssetId !== ""}
          onBackdropClick={() => {
            setSelectedProjectPhotoAssetId("");
            setViewDialogFullScreen(false);
          }}
        >
          <ProjectPhotoViewer
            profileId={profile.id}
            projectPhotos={accountProfile!.projectPhotos}
            onToggleFullScreen={(toggleFullSceenOn: boolean) => {
              setViewDialogFullScreen(toggleFullSceenOn);
            }}
            isEditable={false}
            assetId={selectedProjectPhotoAssetId}
            onProjectPhotosUpdate={(updatedProjectPhotos: ProjectPhoto[]) => {
              setAccountProfile({
                ...accountProfile!,
                projectPhotos: updatedProjectPhotos,
              });
            }}
            onClose={() => {
              setSelectedProjectPhotoAssetId("");
              setViewDialogFullScreen(false);
            }}
          />
        </Dialog>
      ) : null}

      {!isMobile && (
        <div className="fixed left-0 bottom-0 w-full min-h-[100px] flex flex-row bg-white">
          <div className="min-w-[250px] flex left-0 bottom-0">
            <div className="flex flex-col min-h-[100px] max-h-[100px] max-w-[100px] justify-center ml-[32px]">
              <span className="text-xs text-red-3 font-medium mb-[4px]">
                Powered by
              </span>
              <img src={vocsLogo} alt="VOCS" />
            </div>
          </div>
          <div className="flex flex-1 items-center min-h-[100px] profile-viewer-content-question">
            <div className="ml-14 w-full">
              {currentQuestion === "" && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{
                    type: "spring",
                    stiffness: 40,
                    damping: 10,
                  }}
                  className="self-center flex flex-col"
                >
                  <form onSubmit={handleSubmit} className="w-full">
                    <Input
                      ref={inputRef}
                      leftIcon={faComment}
                      label="Ask anything about this profile..."
                      transformValue={trim}
                      onBlur={(input) => console.log(input)}
                      rightButton={{
                        label: "Ask!",
                        type: "submit",
                        onClick: handleAddComment,
                      }}
                      disabled={isLoadingProfileLinkQuestion}
                    />
                  </form>
                </motion.div>
              )}

              {currentQuestion !== "" && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{
                    type: "spring",
                    stiffness: 40,
                    damping: 10,
                  }}
                  className="self-center flex items-center justify-center flex-col"
                >
                  <div className="w-full">
                    <Input
                      ref={inputRef}
                      leftIcon={faComment}
                      label=""
                      transformValue={trim}
                      onBlur={(input) => console.log(input)}
                      value={currentQuestion + "\n\n" + currentAnswer}
                      rows={5}
                    />
                  </div>
                  <Button
                    className="my-2 mt-4"
                    type={"button"}
                    onClick={() => {
                      setCurrentQuestions("");
                      setCurrentAnswer("");
                    }}
                  >
                    Ask a new question
                  </Button>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfileViewer;
